/**
 * Drag-and-drop artwork picker for the custom-art controller.
 *
 * The actual upload is Shopify's, not ours: the file input carries
 * name="properties[...]" and a form="" attribute pointing at the buy-buttons
 * form, so product-form.js sweeps it up in `new FormData(form)` and posts it to
 * /cart/add.js. Shopify stores the file and hands back a /uploads/ URL, which
 * snippets/cart-products.liquid already renders as a link. This file only
 * handles the part the browser won't do for free: dropping, validating, and
 * previewing before the buyer commits.
 */

const FALLBACK_EXTENSIONS = 'png,jpg,jpeg,svg,pdf';

/** Bytes to a short human string, e.g. 4.2 MB. */
function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extensionOf(filename) {
  const dot = filename.lastIndexOf('.');
  return dot === -1 ? '' : filename.slice(dot + 1).toLowerCase();
}

class ArtworkUploadComponent extends HTMLElement {
  #dragDepth = 0;
  #previewUrl = null;
  #copyTimer = null;

  connectedCallback() {
    this.input = this.querySelector('[data-artwork-input]');
    this.zone = this.querySelector('[data-artwork-zone]');
    if (!this.input || !this.zone) return;

    this.meta = this.querySelector('[data-artwork-meta]');
    this.nameEl = this.querySelector('[data-artwork-name]');
    this.sizeEl = this.querySelector('[data-artwork-size]');
    this.errorEl = this.querySelector('[data-artwork-error]');
    this.previewEl = this.querySelector('[data-artwork-preview]');
    this.removeBtn = this.querySelector('[data-artwork-remove]');

    this.root = this.querySelector('[data-artwork-root]');
    this.panel = this.querySelector('[data-artwork-panel]');
    this.maxBytes = Number(this.root?.dataset.maxBytes) || 0;
    this.extensions = (this.root?.dataset.extensions || FALLBACK_EXTENSIONS)
      .split(',')
      .map((ext) => ext.trim().toLowerCase().replace(/^\./, ''))
      .filter(Boolean);

    this.input.addEventListener('change', () => this.#render(this.input.files?.[0] ?? null));
    this.removeBtn?.addEventListener('click', () => this.#clear());

    this.zone.addEventListener('dragenter', this.#onDragEnter);
    this.zone.addEventListener('dragover', this.#onDragOver);
    this.zone.addEventListener('dragleave', this.#onDragLeave);
    this.zone.addEventListener('drop', this.#onDrop);

    this.querySelectorAll('[data-copy]').forEach((button) => {
      button.addEventListener('click', () => this.#copy(button));
    });

    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener('variant:update', this.#onVariantUpdate);
  }

  disconnectedCallback() {
    if (this.#previewUrl) URL.revokeObjectURL(this.#previewUrl);
    if (this.#copyTimer) clearTimeout(this.#copyTimer);
    this.section?.removeEventListener('variant:update', this.#onVariantUpdate);
  }

  // Show or hide with the variant rather than re-rendering, so a file the buyer
  // already picked survives them changing switches or keycaps.
  #onVariantUpdate = (event) => {
    if (!this.root || !this.panel) return;

    // Only the upload panel is gated. The commission strip is a sibling of it
    // and stays visible, because someone has to read it *before* they know to
    // pick custom art.
    const selector = `[data-artwork-block="${this.root.dataset.artworkBlock}"] [data-artwork-panel]`;
    const incoming = event.detail?.data?.html?.querySelector(selector);
    if (!incoming) return;

    const active = incoming.dataset.artworkActive === 'true';
    this.panel.dataset.artworkActive = String(active);
    this.panel.hidden = !active;

    // A hidden input still submits. Disabling keeps a leftover file off an order
    // that no longer includes custom art, without throwing the file away.
    this.input.disabled = !active;
  };

  // Counting enter/leave rather than toggling on each one, because dragleave
  // also fires when the pointer crosses onto a child of the drop zone.
  #onDragEnter = (event) => {
    event.preventDefault();
    this.#dragDepth += 1;
    this.zone.classList.add('is-dragging');
  };

  #onDragOver = (event) => {
    event.preventDefault();
  };

  #onDragLeave = (event) => {
    event.preventDefault();
    this.#dragDepth -= 1;
    if (this.#dragDepth <= 0) {
      this.#dragDepth = 0;
      this.zone.classList.remove('is-dragging');
    }
  };

  #onDrop = (event) => {
    event.preventDefault();
    this.#dragDepth = 0;
    this.zone.classList.remove('is-dragging');

    const file = event.dataTransfer?.files?.[0];
    if (!file) return;

    // The input is what gets submitted, so hand the dropped file over to it.
    const transfer = new DataTransfer();
    transfer.items.add(file);
    this.input.files = transfer.files;
    this.#render(file);
  };

  /** Returns an error string, or null when the file is good to send. */
  #problemWith(file) {
    const extension = extensionOf(file.name);
    if (this.extensions.length && !this.extensions.includes(extension)) {
      const list = this.extensions.map((ext) => ext.toUpperCase()).join(', ');
      return `We cannot use .${extension || 'that'} files. Send one of these instead: ${list}.`;
    }
    if (this.maxBytes && file.size > this.maxBytes) {
      return `That file is ${formatSize(file.size)}. The limit is ${formatSize(
        this.maxBytes
      )}. Export a smaller version and try again.`;
    }
    return null;
  }

  #render(file) {
    this.#releasePreview();

    if (!file) {
      this.#setError(null);
      this.#setAttached(false);
      return;
    }

    const problem = this.#problemWith(file);
    if (problem) {
      this.input.value = '';
      this.#setError(problem);
      this.#setAttached(false);
      return;
    }

    this.#setError(null);
    if (this.nameEl) this.nameEl.textContent = file.name;
    if (this.sizeEl) this.sizeEl.textContent = formatSize(file.size);

    if (this.previewEl && file.type.startsWith('image/')) {
      this.#previewUrl = URL.createObjectURL(file);
      this.previewEl.src = this.#previewUrl;
      this.previewEl.hidden = false;
    }

    this.#setAttached(true);
  }

  #clear() {
    this.input.value = '';
    this.#render(null);
    // The input carries the focus ring that renders on the drop zone.
    this.input.focus();
  }

  #setAttached(attached) {
    this.zone.classList.toggle('is-attached', attached);
    if (this.meta) this.meta.hidden = !attached;
  }

  #setError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message ?? '';
    this.errorEl.hidden = !message;
    this.zone.classList.toggle('is-invalid', Boolean(message));
  }

  #releasePreview() {
    if (this.#previewUrl) {
      URL.revokeObjectURL(this.#previewUrl);
      this.#previewUrl = null;
    }
    if (this.previewEl) {
      this.previewEl.removeAttribute('src');
      this.previewEl.hidden = true;
    }
  }

  async #copy(button) {
    const value = button.dataset.copy;
    const label = button.querySelector('[data-copy-label]') ?? button;
    const original = label.dataset.original ?? label.textContent;
    label.dataset.original = original;

    try {
      await navigator.clipboard.writeText(value);
      label.textContent = button.dataset.copiedLabel || 'Copied';
    } catch {
      // Clipboard access can be blocked; showing the handle is still useful.
      label.textContent = value;
    }

    if (this.#copyTimer) clearTimeout(this.#copyTimer);
    this.#copyTimer = setTimeout(() => {
      label.textContent = original;
    }, 2000);
  }
}

if (!customElements.get('artwork-upload-component')) {
  customElements.define('artwork-upload-component', ArtworkUploadComponent);
}
